import { useState, useEffect } from "react";
import { firstErrorMessage } from "../../../helpers/apiErrors";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAuth } from "src/hooks/useAuth";

type UserData = {
  email: string;
  password: string;
};

export function useSignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_BASE_URL || "https://account-ms.test.hanypay.co";
  const [pendingLoginUserId, setPendingLoginUserId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const userSignIn = async (userData: UserData) => {
    try {
      
      const apiData = {
        ...userData,
        email: userData.email.toLowerCase()
      };
      
      const response = await fetch(`${BASE_URL}/auth/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(apiData),
      });

      const data = await response.json();

      if (data?.otp_page_redirect) {
        if (data.otp_context === "login") {
          setPendingLoginUserId(data.user_id);
          toast.success(data.message || "OTP sent. Please verify to continue.");
          return { otpPending: true };
        }

        navigate("/otp-verify", {
          replace: true,
          state: { user_id: data.user_id, otp_context: data.otp_context || "email_verification" },
        });
        const otpRedirectError = new Error(
          data.message ||
            "OTP not verified. A new OTP has been sent to your email. Please verify to proceed",
        );
        (otpRedirectError as Error & { otpRedirect?: boolean }).otpRedirect = true;
        throw otpRedirectError;
      }

      if (!response.ok) {
        let errorMessage = "An unknown error occurred";
        if (data && typeof data === "object") {
          Object.keys(data).forEach((propertyName) => {
            if (Array.isArray(data[propertyName])) {
              errorMessage = firstErrorMessage(data[propertyName]);
              toast.error(errorMessage);
            }
          });
        }
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  const signInUserMutation = useMutation({
    mutationFn: userSignIn,
    mutationKey: ["SignIn User"],
    onSuccess(data) {
      if (data?.otpPending) {
        return;
      }
      login(data);
    },
    onError(error: Error & { otpRedirect?: boolean }, variables) {
      if (error.otpRedirect) {
        toast.success(error.message || "OTP sent. Please verify to continue.");
        return;
      }
      toast.error(error.message || "Sign-in failed");
    },
  });

  async function handleSignIn(
    values: UserData,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) {
    try {
      await signInUserMutation.mutateAsync(values);
    } catch {
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyLoginOtp() {
    if (!pendingLoginUserId) {
      toast.error("Please sign in again to request a login OTP.");
      return;
    }

    if (otpCode.trim().length !== 8) {
      toast.error("Please enter the 8-digit OTP sent to your email.");
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await fetch(`${BASE_URL}/auth/login/otp-verify/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: pendingLoginUserId, otp: otpCode.trim() }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "OTP verification failed");
      }

      login(data);
    } catch (error: any) {
      toast.error(error?.message || "OTP verification failed");
    } finally {
      setIsVerifyingOtp(false);
    }
  }

  async function handleResendLoginOtp() {
    if (!pendingLoginUserId || resendTimer > 0) return;
    setIsResendingOtp(true);
    try {
      await fetch(`${BASE_URL}/common/user/${pendingLoginUserId}/resend-otp/`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      toast.success("A new OTP has been sent to your email.");
      setOtpCode("");
      setResendTimer(60);
    } catch {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsResendingOtp(false);
    }
  }

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  return {
    initialValues: {
      email: "",
      password: "",
    },
    handleSignIn,
    handleVerifyLoginOtp,
    handleResendLoginOtp,
    pendingLoginUserId,
    otpCode,
    setOtpCode,
    isVerifyingOtp,
    isResendingOtp,
    resendTimer,
  };
}
